/**
 * Record-access tag chips for the entity-detail header (DSCO-5543 — Node Based
 * Access Control, HLD v0.3.1 Design C).
 *
 * Dimension-generic: the record's tags live under the active dimension's
 * `recordTagField` (`teamIds` on FR today, `nodeIds` on a node-based client),
 * and their labels and colours come from the same curated `/search` response
 * that populates the selector — never from a second lookup and never from
 * client-side hashing.
 *
 * Labels are backend/config-driven data, which the repo i18n convention
 * excludes from client-side translation, so nothing here is `t()`-wrapped.
 */

import { useMemo } from 'react';
import { Chip, Stack } from '@locus-taxy/disco-ui';
import { useClientId } from '../../hooks/useClientId';
import { useCurrentUser } from '../../api/endpoints/useCurrentUser';
import { useAccessDimensionOptions } from '../../api/endpoints/useAccessDimensionOptions';

/**
 * Tier 2 of Design C's two-tier colour rule, used when `/search` sends no
 * `color` for a value. A disco-ui token rather than a frozen hex, so the
 * fallback tracks the design system across both themes.
 */
const NEUTRAL_ACCENT_COLOR = 'var(--color-status-grey-bold)';

interface EntityAccessDimensionChipsProps {
  /**
   * The record's `entityData`. Read defensively: it is absent before the
   * instance lands, and the active dimension's tag field may be missing from a
   * record that carries no tags.
   */
  entityData?: Record<string, unknown>;
}

/**
 * The record's tag ids on the active dimension's field.
 *
 * `entityData` is an open record and `recordTagField` is only known at runtime,
 * so the read is narrowed here rather than by widening any entity type — an
 * index signature on a typed entity would silence field-name typos everywhere
 * else. An absent or non-array field, and any non-string element, is dropped.
 */
function readTagIds(
  entityData: Record<string, unknown> | undefined,
  recordTagField: string | undefined
): string[] {
  if (!entityData || !recordTagField) {
    return [];
  }

  const tagIds = entityData[recordTagField];
  if (!Array.isArray(tagIds)) {
    return [];
  }

  return tagIds.filter((tagId): tagId is string => typeof tagId === 'string');
}

export default function EntityAccessDimensionChips({
  entityData,
}: Readonly<EntityAccessDimensionChipsProps>) {
  const clientId = useClientId();
  const { activeDimension } = useCurrentUser(clientId);

  // Same query key as the selector's read — `['accessDimensionOptions',
  // clientId, name]` — so react-query serves both from one `/search`; these
  // chips add no request of their own. The name is empty until `/me` lands, and
  // stays empty when enforcement is disabled, which keeps the query disabled.
  const { data: options } = useAccessDimensionOptions(
    clientId,
    activeDimension?.name ?? ''
  );

  const optionsById = useMemo(
    () => new Map(options.map(option => [option.id, option])),
    [options]
  );

  const tagIds = readTagIds(entityData, activeDimension?.recordTagField);

  // Nothing to render → no wrapper either, so a peek or header layout never
  // inherits an empty Stack's gap.
  if (tagIds.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" gap={1} align="center" wrap>
      {tagIds.map(tagId => {
        const option = optionsById.get(tagId);
        return (
          // disco-ui Chip "subtle" variant — light/neutral body matching the
          // entity-detail template's tag chip — with the value's colour as the
          // accent (left bar), instead of the heavy "filled" body.
          <Chip
            key={tagId}
            size="sm"
            variant="subtle"
            label={option?.label ?? tagId}
            accentColor={option?.color ?? NEUTRAL_ACCENT_COLOR}
          />
        );
      })}
    </Stack>
  );
}
